<template>
  <div>
    <b-modal
      id="convocation-qr-modal"
      title="Convocation registration link"
      centered
      size="sm"
      footer-class="justify-content-center"
    >
      <div class="d-flex justify-content-center py-1">
        <qrcode-vue :value="publicLinkUrl" :size="200" level="H" />
      </div>
      <template #modal-footer>
        <b-button variant="success" class="btn-icon" @click="downloadQr">
          <feather-icon icon="DownloadIcon" />
          <span class="align-middle ml-50">Download</span>
        </b-button>
        <b-button variant="primary" class="btn-icon" @click="copyLink">
          <feather-icon icon="CopyIcon" />
          <span class="align-middle ml-50">Copy</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="convocation-view-modal"
      v-model="showViewModal"
      title="Registration details"
      centered
      size="md"
      scrollable
      body-class="view-reg-modal-body"
      footer-class="view-reg-modal__footer"
      modal-class="convocation-view-modal"
    >
      <div v-if="viewRecord" class="view-reg-modal">
        <div class="view-reg-modal__hero">
          <img
            v-if="viewRecord.photoUrl"
            :src="viewRecord.photoUrl"
            alt=""
            class="view-reg-photo"
            referrerpolicy="no-referrer"
          />
          <div v-else class="view-reg-photo view-reg-photo--placeholder">
            <feather-icon icon="UserIcon" size="28" class="text-muted" />
          </div>
          <div class="view-reg-modal__title-block min-w-0">
            <h5 class="view-reg-modal__name mb-25">
              {{ viewRecord.firstName }} {{ viewRecord.lastName }}
            </h5>
            <div class="view-reg-modal__meta">
              <b-badge variant="light-primary" class="mr-25">
                Class of {{ viewRecord.convocationYear || "—" }}
              </b-badge>
              <b-badge variant="light-info" class="mr-25">
                {{ viewRecord.departmentName || "—" }}
              </b-badge>
              <span class="text-muted small">
                · Submitted {{ formatDisplayDate(viewRecord.addedOn) }}
              </span>
            </div>
          </div>
        </div>

        <div class="view-reg-dense-grid">
          <div v-if="hasViewText(viewRecord.cnic)" class="view-reg-cell">
            <span class="view-reg-cell__label">CNIC</span>
            <span class="view-reg-cell__value">{{ viewRecord.cnic }}</span>
          </div>
          <div v-if="hasViewText(viewRecord.regNo)" class="view-reg-cell">
            <span class="view-reg-cell__label">KSA Reg #</span>
            <span class="view-reg-cell__value">{{ viewRecord.regNo }}</span>
          </div>
          <div v-if="hasViewText(viewRecord.dob)" class="view-reg-cell">
            <span class="view-reg-cell__label">Date of birth</span>
            <span class="view-reg-cell__value">{{ formatDisplayDob(viewRecord.dob) }}</span>
          </div>
          <div v-if="hasViewText(viewRecord.gender)" class="view-reg-cell">
            <span class="view-reg-cell__label">Gender</span>
            <span class="view-reg-cell__value">{{ viewRecord.gender }}</span>
          </div>
          <div v-if="hasViewText(viewRecord.cellPhone)" class="view-reg-cell">
            <span class="view-reg-cell__label">Cell / WhatsApp</span>
            <span class="view-reg-cell__value">{{ viewRecord.cellPhone }}</span>
          </div>
          <div v-if="hasViewText(viewRecord.email)" class="view-reg-cell">
            <span class="view-reg-cell__label">Email</span>
            <span class="view-reg-cell__value">{{ viewRecord.email }}</span>
          </div>
          <div v-if="hasViewText(viewRecord.tel)" class="view-reg-cell">
            <span class="view-reg-cell__label">Tel</span>
            <span class="view-reg-cell__value">{{ viewRecord.tel }}</span>
          </div>
          <div v-if="viewFullAddress" class="view-reg-cell view-reg-cell--full">
            <span class="view-reg-cell__label">Address</span>
            <span class="view-reg-cell__value">{{ viewFullAddress }}</span>
          </div>
        </div>

        <template v-if="viewGuestsWithData.length">
          <div class="view-reg-guests-head">Guests</div>
          <table class="table table-sm view-guest-table mb-0">
            <thead>
              <tr>
                <th class="py-50">#</th>
                <th class="py-50">Name</th>
                <th class="py-50">Relationship</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(g, idx) in viewGuestsWithData" :key="'view-g-' + idx">
                <td class="py-50">{{ idx + 1 }}</td>
                <td class="py-50">{{ g.name || "—" }}</td>
                <td class="py-50">{{ g.relationship || "—" }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false">
          Close
        </b-button>
        <b-button
          v-if="viewRecord"
          variant="outline-primary"
          class="mr-50"
          @click="printIndividualRegistration(viewRecord)"
        >
          <feather-icon icon="PrinterIcon" size="14" class="mr-50" />
          Print
        </b-button>
        <b-button
          v-if="rights.edit && viewRecord"
          variant="gradient-primary"
          @click="openEditFromView"
        >
          Edit registration
        </b-button>
      </template>
    </b-modal>

    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header mb-2">
        <h5 class="mb-0 resource-title">Convocation Registration</h5>
        <small class="text-muted">
          Public form link and registration records
        </small>
      </div>

      <!-- Form options sidebar -->
      <b-sidebar
        id="convocation-setup-sidebar"
        v-model="showSetupPanel"
        bg-variant="white"
        sidebar-class="sidebar-lg"
        right
        no-header
        backdrop
        shadow
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1 border-bottom"
        >
          <h5 class="mb-0">Form options</h5>
          <feather-icon
            class="cursor-pointer"
            icon="XIcon"
            size="18"
            @click="showSetupPanel = false"
          />
        </div>
        <div class="p-2 setup-sidebar-body">
          <p class="text-muted small mb-1">
            Departments and <strong>Class of</strong> years on the public form.
          </p>
          <label class="form-label small font-weight-bold mb-50">Departments (from classes)</label>
          <v-select
            v-model="setupForm.selectedClassIds"
            :options="classOptions"
            label="name"
            :reduce="(val) => val.id"
            multiple
            placeholder="Select classes"
            class="planner-filter-select mb-1"
            :loading="classesLoading"
          />
          <label class="form-label small font-weight-bold mb-50">Class of</label>
          <div class="d-flex flex-wrap year-chip-row mb-2">
            <b-form-checkbox
              v-for="year in yearOptions"
              :key="'setup-year-' + year"
              v-model="setupForm.years"
              :value="year"
              class="mr-2 mb-0"
            >
              {{ year }}
            </b-form-checkbox>
          </div>
          <b-button
            block
            variant="gradient-primary"
            :disabled="setupSaving || !canSaveSetup"
            @click="saveSetup"
          >
            <b-spinner v-if="setupSaving" small class="mr-50" />
            Save options
          </b-button>
        </div>
      </b-sidebar>

      <!-- Edit / add registration -->
      <b-sidebar
        id="convocation-reg-sidebar"
        v-model="showRegPanel"
        bg-variant="white"
        sidebar-class="sidebar-lg"
        right
        no-header
        backdrop
        shadow
      >
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1 border-bottom"
        >
          <h5 class="mb-0">Edit registration</h5>
          <feather-icon
            class="cursor-pointer"
            icon="XIcon"
            size="18"
            @click="showRegPanel = false"
          />
        </div>
        <b-form class="p-2 reg-edit-form" @submit.prevent="saveRegistration">
          <b-row class="mb-2">
            <b-col cols="12" class="d-flex justify-content-center">
              <b-form-group label=" " label-class="d-none" class="mb-0">
                <b-media no-body class="justify-content-center">
                  <div
                    v-if="!regPhotoPreview && !regPhotoUploading"
                    class="photo-circle photo-circle--empty"
                    @click="triggerRegPhotoInput"
                  >
                    <b-img
                      src="https://cdn.cloudious.net/file-1677912450367-333289208.png"
                      rounded="circle"
                      height="100"
                      width="100"
                    />
                  </div>
                  <div
                    v-else-if="regPhotoUploading"
                    class="photo-circle photo-circle--empty"
                  >
                    <b-spinner variant="primary" type="grow" />
                  </div>
                  <div v-else class="photo-circle photo-circle--loaded">
                    <img
                      :src="regPhotoPreview"
                      alt="Student photo"
                      class="photo-circle-img"
                      referrerpolicy="no-referrer"
                      @click="triggerRegPhotoInput"
                    />
                    <b-button
                      variant="danger"
                      class="photo-delete-btn btn-icon rounded-circle"
                      size="sm"
                      @click.stop="clearRegPhoto"
                    >
                      <feather-icon icon="Trash2Icon" size="14" />
                    </b-button>
                  </div>
                  <input
                    ref="regPhotoInput"
                    type="file"
                    accept="image/*"
                    hidden
                    @change="onRegPhotoSelected"
                  />
                </b-media>
                <h6 class="mb-0 mt-1 text-center text-muted small">
                  {{ regPhotoUploading ? "Uploading..." : "Upload photo" }}
                </h6>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Class of" label-class="font-weight-bold">
            <div class="d-flex flex-wrap">
              <b-form-radio
                v-for="year in yearOptions"
                :key="'reg-year-' + year"
                v-model="regConvocationYear"
                name="reg-convocation-class-year"
                :value="year"
                class="mr-2"
              >
                {{ year }}
              </b-form-radio>
            </div>
          </b-form-group>

          <b-form-group label="Department" label-class="font-weight-bold">
            <b-form-radio
              v-for="dept in editDepartmentOptions"
              :key="'reg-dept-' + dept"
              v-model="regForm.departmentName"
              name="reg-convocation-department"
              :value="dept"
              class="mb-50"
            >
              {{ dept }}
            </b-form-radio>
            <b-form-input
              v-if="!editDepartmentOptions.length"
              v-model="regForm.departmentName"
              placeholder="Department name"
            />
          </b-form-group>

          <hr class="my-1" />
          <h6 class="font-weight-bold text-uppercase small mb-1">Personal information</h6>

          <b-row>
            <b-col md="6">
              <b-form-group label="First name" label-class="font-weight-bold">
                <b-form-input v-model="regForm.firstName" required />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Last name" label-class="font-weight-bold">
                <b-form-input v-model="regForm.lastName" required />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="CNIC" label-class="font-weight-bold">
                <b-form-input v-model="regForm.cnic" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Reg #" label-class="font-weight-bold">
                <b-form-input v-model="regForm.regNo" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Date of birth" label-class="font-weight-bold">
                <b-form-input v-model="regForm.dob" type="date" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Gender" label-class="font-weight-bold">
                <b-form-select v-model="regForm.gender" :options="genderOptions" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Address line 1" label-class="font-weight-bold">
                <b-form-input v-model="regForm.addressLine1" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Address line 2" label-class="font-weight-bold">
                <b-form-input v-model="regForm.addressLine2" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Tel" label-class="font-weight-bold">
                <b-form-input v-model="regForm.tel" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Cell / WhatsApp" label-class="font-weight-bold">
                <b-form-input v-model="regForm.cellPhone" />
              </b-form-group>
            </b-col>
            <b-col cols="12">
              <b-form-group label="Email" label-class="font-weight-bold">
                <b-form-input v-model="regForm.email" type="email" />
              </b-form-group>
            </b-col>
          </b-row>

          <hr class="my-1" />
          <h6 class="font-weight-bold text-uppercase small mb-1">Guest details</h6>
          <b-row
            v-for="(g, idx) in regForm.guests"
            :key="'reg-guest-' + idx"
            class="align-items-center mb-1"
          >
            <b-col cols="12" md="3">
              <span class="small font-weight-bold">Guest {{ idx + 1 }}</span>
            </b-col>
            <b-col md="4">
              <b-form-input v-model="g.name" placeholder="Name" size="sm" />
            </b-col>
            <b-col md="5">
              <b-form-input
                v-model="g.relationship"
                placeholder="Relationship"
                size="sm"
              />
            </b-col>
          </b-row>

          <b-button
            type="submit"
            variant="gradient-primary"
            block
            class="mt-2"
            :disabled="regSaving"
          >
            <b-spinner v-if="regSaving" small class="mr-50" />
            Save changes
          </b-button>
        </b-form>
      </b-sidebar>

      <div class="d-flex align-items-center convocation-search-row mb-2">
        <b-input-group class="convocation-search-group flex-grow-1">
          <b-input-group-prepend is-text>
            <feather-icon icon="SearchIcon" size="16" />
          </b-input-group-prepend>
          <b-form-input
            v-model="searchQuery"
            placeholder="Search registrations..."
            @keyup.enter="onSearch"
            @input="debouncedSearch"
          />
        </b-input-group>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="gradient-primary"
          class="action-btn-filter ml-1 text-nowrap flex-shrink-0"
          v-b-tooltip.hover.top="'Print overall convocation report'"
          @click="printOverallReport"
        >
          <feather-icon icon="PrinterIcon" size="14" class="mr-50" />
          Print report
        </b-button>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="outline-secondary"
          class="convocation-qr-btn btn-icon p-0 ml-1 flex-shrink-0"
          v-b-tooltip.hover.top="'Copy or download registration QR'"
          @click="openQrModal"
        >
          <img src="@/assets/images/icons/qr-code.png" width="34" alt="QR" />
        </b-button>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="regPerPage"
              :options="pageSizeOptions"
              :reduce="(o) => o.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onRegPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ regRange.start }}</strong> to
            <strong>{{ regRange.end }}</strong> of
            <strong>{{ regTotal }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="regPage"
            :total-rows="regTotal"
            :per-page="regPerPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadRegistrations"
          />
        </div>
      </div>

      <b-table
        class="planner-list-table"
        :items="registrations"
        :fields="regFields"
        :busy="regLoading"
        show-empty
        small
        hover
        responsive
      >
        <template #table-busy>
          <div class="list-skeleton">
            <div
              v-for="n in 6"
              :key="'skeleton-' + n"
              class="list-skeleton__row"
              :style="{
                gridTemplateColumns: `repeat(${regFields.length}, minmax(0, 1fr))`,
              }"
            >
              <div
                v-for="(field, index) in regFields"
                :key="'skeleton-cell-' + n + '-' + index"
                class="list-skeleton__cell"
              >
                <div
                  class="list-skeleton__bar"
                  :style="{ width: skeletonBarWidth(index) }"
                ></div>
              </div>
            </div>
          </div>
        </template>

        <template #empty>
          <p class="text-center text-muted my-2 mb-0">No registrations yet.</p>
        </template>
        <template #head(actions)="data">
          <div class="text-right">
            <span>{{ data.label }}</span>
          </div>
        </template>
        <template #cell(sno)="{ index }">
          {{ (regPage - 1) * regPerPage + index + 1 }}
        </template>
        <template #cell(fullName)="data">
          {{ data.item.firstName }} {{ data.item.lastName }}
        </template>
        <template #cell(departmentName)="data">
          {{ data.item.departmentName || "—" }}
        </template>
        <template #cell(addedOn)="data">
          {{ formatDisplayDate(data.item.addedOn) }}
        </template>
        <template #cell(actions)="{ item }">
          <div class="d-flex justify-content-end convocation-actions-cell">
            <b-button
              v-if="rights.view"
              variant="outline-primary"
              v-b-tooltip.hover.top
              title="View"
              class="action-btn-outline mr-1"
              @click="viewRegistration(item)"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
            <b-button
              variant="outline-secondary"
              v-b-tooltip.hover.top
              title="Print"
              class="action-btn-outline mr-1"
              @click="printIndividualRegistration(item)"
            >
              <feather-icon icon="PrinterIcon" />
            </b-button>
            <b-button
              v-if="rights.edit"
              variant="gradient-primary"
              v-b-tooltip.hover.top
              title="Edit"
              class="action-btn-table mr-1"
              @click="editRegistration(item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
            <b-button
              v-if="rights.delete"
              variant="outline-danger"
              v-b-tooltip.hover.top
              title="Delete"
              class="action-btn-outline delete-btn"
              @click="deleteRegistration(item)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BSpinner,
  BFormInput,
  BSidebar,
  BModal,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BFormRadio,
  BFormSelect,
  BInputGroup,
  BInputGroupPrepend,
  BImg,
  BMedia,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import QrcodeVue from "qrcode.vue";
import { mapActions } from "vuex";
import Ripple from "vue-ripple-directive";
import { getUploadedPaths, uploadToMySkool } from "@/utils/gdriveUpload";

const SETTING_KEY = "convocation_form";

const DEFAULT_DEPARTMENTS = [
  "Fine Arts",
  "Communication Design",
  "Textile Design",
  "Digital Media",
];

function emptyGuests() {
  return Array.from({ length: 5 }, () => ({ name: "", relationship: "" }));
}

function emptyRegForm(campusID) {
  return {
    id: 0,
    campusID,
    departmentName: "",
    convocationYear: "",
    firstName: "",
    lastName: "",
    cnic: "",
    regNo: "",
    dob: "",
    gender: "",
    addressLine1: "",
    addressLine2: "",
    tel: "",
    cellPhone: "",
    email: "",
    photoUrl: "",
    guests: emptyGuests(),
  };
}

export default {
  directives: {
    Ripple,
    "b-tooltip": VBTooltip,
  },
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BFormInput,
    BSidebar,
    BModal,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BFormRadio,
    BFormSelect,
    BInputGroup,
    BInputGroupPrepend,
    BImg,
    BMedia,
    BBadge,
    vSelect,
    QrcodeVue,
  },
  data() {
    return {
      showSetupPanel: false,
      showViewModal: false,
      viewRecord: null,
      showRegPanel: false,
      classOptions: [],
      classesLoading: false,
      yearOptions: [2024, 2025, 2026],
      setupForm: {
        selectedClassIds: [],
        years: [],
        departmentNames: [],
      },
      setupSaving: false,
      settingRecord: null,
      searchQuery: "",
      registrations: [],
      regLoading: true,
      regSaving: false,
      regPage: 1,
      regPerPage: 20,
      regTotal: 0,
      regForm: emptyRegForm(0),
      regFields: [
        {
          key: "sno",
          label: "S.No",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "fullName", label: "Name" },
        { key: "departmentName", label: "Department" },
        { key: "convocationYear", label: "Year" },
        { key: "cellPhone", label: "Cell" },
        { key: "email", label: "Email" },
        { key: "addedOn", label: "Submitted" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-right convocation-actions-col",
          tdClass: "text-right convocation-actions-col",
        },
      ],
      rights: {
        view: true,
        add: true,
        edit: true,
        delete: true,
      },
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
      ],
      searchDebounceTimer: null,
      regConvocationYear: null,
      regPhotoPreview: "",
      regPhotoUploading: false,
      genderOptions: [
        { value: "", text: "Select gender" },
        { value: "Male", text: "Male" },
        { value: "Female", text: "Female" },
        { value: "Other", text: "Other" },
      ],
    };
  },
  computed: {
    editDepartmentOptions() {
      const list = this.setupForm.departmentNames;
      if (Array.isArray(list) && list.length) return list;
      return DEFAULT_DEPARTMENTS;
    },
    publicLinkUrl() {
      const origin = window.location.origin;
      const db = this.$store.state.userData.db;
      const cId = this.$store.state.userData.cId;
      return `${origin}/convocation-registration?db=${encodeURIComponent(
        db
      )}&cId=${cId}`;
    },
    canSaveSetup() {
      return (
        this.setupForm.selectedClassIds.length > 0 &&
        this.setupForm.years.length > 0
      );
    },
    regRange() {
      if (!this.regTotal) return { start: 0, end: 0 };
      const start = (this.regPage - 1) * this.regPerPage + 1;
      const end = Math.min(this.regPage * this.regPerPage, this.regTotal);
      return { start, end };
    },
    viewGuestsWithData() {
      if (!this.viewRecord?.guests) return [];
      return this.viewRecord.guests.filter(
        (g) => String(g.name || "").trim() || String(g.relationship || "").trim()
      );
    },
    viewFullAddress() {
      if (!this.viewRecord) return "";
      return [this.viewRecord.addressLine1, this.viewRecord.addressLine2]
        .filter((s) => String(s || "").trim())
        .join(", ");
    },
  },
  async created() {
    this.initRightsFromMenu();
    this.regForm.campusID = this.$store.state.userData.cId;
    // Pagination first so table skeleton shows immediately (planner pattern)
    const tablePromise = this.loadRegistrations();
    const classesPromise = this.loadClasses();
    await classesPromise;
    await Promise.all([tablePromise, this.loadConvocationSetting()]);
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete" }),

    initRightsFromMenu() {
      const right = {};
      (this.$store.state.menu || []).forEach((el) => {
        (el.children || []).forEach((ch) => {
          right[ch.route] = {
            view: ch.view,
            add: ch.add,
            edit: ch.edit,
            delete: ch.delete,
          };
        });
      });
      this.$store.commit("setRights", right);
      const routeRights = this.$store.state.rights[this.$route.name];
      if (routeRights) {
        this.rights = { ...this.rights, ...routeRights };
      }
    },

    openQrModal() {
      this.$bvModal.show("convocation-qr-modal");
    },

    downloadQr() {
      const canvas = document.querySelector("#convocation-qr-modal canvas");
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = "convocation-registration.png";
      link.href = canvas.toDataURL();
      link.click();
    },

    async loadClasses() {
      this.classesLoading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        });
        const list = Array.isArray(res) ? res : [];
        this.classOptions = list.map((el) => el.cls || el);
      } catch (e) {
        console.error(e);
        this.classOptions = [];
      } finally {
        this.classesLoading = false;
      }
    },

    parseYears(raw) {
      if (Array.isArray(raw)) return raw.map(Number).filter(Boolean);
      if (typeof raw === "string") {
        try {
          const p = JSON.parse(raw);
          return Array.isArray(p) ? p.map(Number).filter(Boolean) : [];
        } catch {
          return raw
            .split(",")
            .map((y) => parseInt(y, 10))
            .filter(Boolean);
        }
      }
      return [];
    },

    applySettingValue() {
      if (!this.settingRecord?.value) return;
      try {
        const payload = JSON.parse(this.settingRecord.value);
        this.setupForm.years = this.parseYears(payload.years);
        if (Array.isArray(payload.departmentNames)) {
          this.setupForm.departmentNames = [...payload.departmentNames];
          this.setupForm.selectedClassIds = this.classOptions
            .filter((c) => payload.departmentNames.includes(c.name))
            .map((c) => c.id);
        } else if (Array.isArray(payload.selectedClassIds)) {
          this.setupForm.selectedClassIds = [...payload.selectedClassIds];
          this.syncDepartmentNamesFromClasses();
        }
      } catch {
        /* ignore */
      }
    },

    syncDepartmentNamesFromClasses() {
      this.setupForm.departmentNames = this.setupForm.selectedClassIds
        .map((id) => this.classOptions.find((c) => c.id === id)?.name)
        .filter(Boolean);
    },

    async loadConvocationSetting() {
      try {
        const res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettings?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: [SETTING_KEY],
          token: this.$store.state.userData.token,
          subjects: true,
        });
        this.settingRecord = Array.isArray(res)
          ? res.find((el) => el.key === SETTING_KEY) || null
          : null;
        this.applySettingValue();
        if (!this.setupForm.years.length) {
          this.setupForm.years = [...this.yearOptions];
        }
      } catch (e) {
        console.error(e);
        this.settingRecord = null;
      }
    },

    async saveSetup() {
      if (!this.canSaveSetup) return;
      this.setupSaving = true;
      try {
        this.syncDepartmentNamesFromClasses();
        const payload = {
          years: this.setupForm.years,
          departmentNames: this.setupForm.departmentNames,
          selectedClassIds: this.setupForm.selectedClassIds,
          url: this.publicLinkUrl,
          updatedAt: new Date().toISOString(),
        };
        const settObj = this.settingRecord
          ? { ...this.settingRecord, value: JSON.stringify(payload) }
          : {
              id: 0,
              campusID: this.$store.state.userData.cId,
              key: SETTING_KEY,
              value: JSON.stringify(payload),
              valueBool: true,
              valueNumber: 0,
            };
        await this.post({
          url:
            this.$store.state.domain +
            "Settings/SaveNew?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: [settObj],
          token: this.$store.state.userData.token,
          message: "Form options saved.",
          context: this,
        });
        this.settingRecord = settObj;
        this.showSetupPanel = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.setupSaving = false;
      }
    },

    async loadRegistrations() {
      this.regLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "ConvocationRegistration/GetByPagination?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&page=" +
            this.regPage +
            "&pageSize=" +
            this.regPerPage +
            "&search=" +
            encodeURIComponent(this.searchQuery || ""),
          token: this.$store.state.userData.token,
          dashboard: true,
        };
        const res = await this.get(obj);
        let rows = [];
        let total = 0;
        if (res?.status === "success" && Array.isArray(res.data)) {
          rows = res.data;
          total = res.total || res.totalCount || rows.length;
        } else if (Array.isArray(res?.data)) {
          rows = res.data;
          total = res.total || rows.length;
        }
        this.registrations = rows;
        this.regTotal = total;
      } catch (e) {
        console.error(e);
        this.registrations = [];
        this.regTotal = 0;
      } finally {
        this.regLoading = false;
      }
    },

    onSearch() {
      this.regPage = 1;
      this.loadRegistrations();
    },

    debouncedSearch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.onSearch(), 400);
    },

    onRegPageSizeChange() {
      this.regPage = 1;
      this.loadRegistrations();
    },

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },

    parseConvocationYears(raw) {
      if (Array.isArray(raw)) return raw.map(Number).filter(Boolean);
      return String(raw || "")
        .split(",")
        .map((y) => parseInt(y, 10))
        .filter(Boolean);
    },

    parseGuestsFromRecord(item) {
      if (!item) return [];
      if (Array.isArray(item.guests) && item.guests.length) {
        return item.guests;
      }
      const raw = item.guestsJson ?? item.GuestsJson ?? item.guests_json;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    },

    normalizeRegGuests(guests) {
      const list = Array.isArray(guests) ? guests : [];
      return Array.from({ length: 5 }, (_, i) => ({
        name: list[i]?.name || "",
        relationship: list[i]?.relationship || "",
      }));
    },

    formatDobForInput(value) {
      if (!value) return "";
      const s = String(value).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    },

    triggerRegPhotoInput() {
      if (!this.regPhotoUploading) this.$refs.regPhotoInput?.click();
    },

    clearRegPhoto() {
      this.regPhotoPreview = "";
      this.regForm.photoUrl = "";
      if (this.$refs.regPhotoInput) this.$refs.regPhotoInput.value = "";
    },

    async onRegPhotoSelected(event) {
      const files = event.target?.files;
      if (!files?.length) return;
      this.regPhotoUploading = true;
      try {
        const uploaded = await uploadToMySkool(files);
        const paths = getUploadedPaths(uploaded);
        if (paths.length) {
          this.regForm.photoUrl = paths[0];
          this.regPhotoPreview = paths[0];
        }
      } catch (e) {
        console.error(e);
        this.$bvToast.toast("Photo upload failed.", {
          title: "Upload",
          variant: "warning",
          solid: true,
        });
      } finally {
        this.regPhotoUploading = false;
        if (this.$refs.regPhotoInput) this.$refs.regPhotoInput.value = "";
      }
    },

    hasViewText(val) {
      return String(val ?? "").trim().length > 0;
    },

    viewRegistration(item) {
      if (!this.rights.view) return;
      this.viewRecord = {
        ...item,
        guests: this.normalizeRegGuests(this.parseGuestsFromRecord(item)),
      };
      this.showViewModal = true;
    },

    openEditFromView() {
      if (!this.viewRecord || !this.rights.edit) return;
      const item = { ...this.viewRecord };
      this.showViewModal = false;
      this.$nextTick(() => this.editRegistration(item));
    },

    formatDisplayDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) {
        const s = String(val).trim();
        return s || "—";
      }
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatDisplayDob(val) {
      if (!val) return "—";
      const s = String(val).trim();
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const d = new Date(`${s.slice(0, 10)}T12:00:00`);
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
        }
      }
      return this.formatDisplayDate(val);
    },

    editRegistration(item) {
      if (!this.rights.edit) return;
      const guests = this.normalizeRegGuests(this.parseGuestsFromRecord(item));
      this.regForm = {
        ...emptyRegForm(this.$store.state.userData.cId),
        ...item,
        dob: this.formatDobForInput(item.dob),
        guests,
      };
      const years = this.parseConvocationYears(item.convocationYear);
      this.regConvocationYear = years.length ? years[0] : null;
      this.regPhotoPreview = item.photoUrl || "";
      this.regPhotoUploading = false;
      this.showRegPanel = true;
    },

    async saveRegistration() {
      if (!this.regForm.firstName?.trim() || !this.regForm.lastName?.trim()) {
        this.$bvToast.toast("First and last name are required.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.regSaving = true;
      try {
        const body = { ...this.regForm };
        body.campusID = Number(this.$store.state.userData.cId) || 0;
        body.id = Number(body.id) || 0;
        body.convocationYear =
          this.regConvocationYear != null && this.regConvocationYear !== ""
            ? String(this.regConvocationYear)
            : "";
        body.photoUrl = this.regForm.photoUrl || this.regPhotoPreview || "";
        body.guests = this.regForm.guests.map((g) => ({
          name: g.name || "",
          relationship: g.relationship || "",
        }));
        delete body.guestsJson;
        delete body.GuestsJson;
        delete body.addedOn;
        delete body.updatedOn;
        await this.post({
          url:
            this.$store.state.domain +
            "ConvocationRegistration/Save?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId,
          body,
          token: this.$store.state.userData.token,
          message: "Registration saved.",
          context: this,
        });
        this.showRegPanel = false;
        await this.loadRegistrations();
      } catch (e) {
        console.error(e);
      } finally {
        this.regSaving = false;
      }
    },

    async deleteRegistration(item) {
      if (!this.rights.delete) return;
      const ok = await this.$swal.fire({
        title: "Delete this registration?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
      });
      if (!ok.isConfirmed) return;
      try {
        await this.delete({
          url:
            this.$store.state.domain +
            "ConvocationRegistration/Delete?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&id=" +
            item.id,
          token: this.$store.state.userData.token,
          message: "Deleted.",
          context: this,
        });
        await this.loadRegistrations();
      } catch (e) {
        console.error(e);
      }
    },

    copyLink() {
      navigator.clipboard.writeText(this.publicLinkUrl).then(() => {
        this.$bvToast.toast("Link copied.", {
          title: "Copied",
          variant: "success",
          solid: true,
        });
      });
    },

    openConvocationReport(query) {
      const routeData = this.$router.resolve({
        name: "convocation-registration-report",
        query,
      });
      window.open(routeData.href, "_blank");
    },

    printOverallReport() {
      this.openConvocationReport({
        db: this.$store.state.userData.db,
        campusID: this.$store.state.userData.cId,
      });
    },

    printIndividualRegistration(item) {
      if (!item || !item.id) return;
      this.openConvocationReport({
        db: this.$store.state.userData.db,
        campusID: this.$store.state.userData.cId,
        id: item.id,
      });
    },

    formatDate(val) {
      return this.formatDisplayDate(val);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/myskool/planner/plannerListTable.scss";

.convocation-search-row {
  gap: 0.5rem;
  width: 100%;
}

.convocation-search-group {
  min-width: 0;
}

.convocation-qr-btn {
  min-width: 42px;
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.357rem;
}

.setup-sidebar-body .year-chip-row {
  gap: 0.25rem 1rem;
}

.reg-edit-form ::v-deep .form-group {
  margin-bottom: 1rem;
}

.photo-circle {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.photo-circle--empty {
  cursor: pointer;
}

.photo-circle-img {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ebebeb;
  cursor: pointer;
}

.photo-delete-btn {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0.35rem;
}

.convocation-actions-col {
  min-width: 148px;
  white-space: nowrap;
}

.convocation-actions-cell {
  padding-right: 0.25rem;
}

::v-deep .view-reg-modal-body {
  padding: 0.85rem 1.1rem 0.65rem;
}

.view-reg-modal__hero {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.65rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #ebe9f1;
}

.view-reg-photo {
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0d8ff;
}

.view-reg-photo--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #faf9ff;
}

.view-reg-modal__name {
  color: #2d1b69;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.25;
}

.view-reg-modal__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  line-height: 1.35;
}

.view-reg-dense-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
}

@media (min-width: 576px) {
  .view-reg-dense-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.view-reg-cell__label {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #a8a4b5;
  line-height: 1.2;
}

.view-reg-cell__value {
  display: block;
  font-size: 0.8125rem;
  color: #2d1b69;
  font-weight: 500;
  line-height: 1.35;
  word-break: break-word;
}

.view-reg-cell--full {
  grid-column: 1 / -1;
}

.view-reg-guests-head {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #6e6b7b;
  margin: 0.65rem 0 0.35rem;
}

.view-guest-table {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  overflow: hidden;

  thead th {
    background: #faf9ff;
    border-bottom: 1px solid #e0d8ff;
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #6e6b7b;
    font-weight: 600;
  }

  tbody td {
    font-size: 0.8125rem;
    vertical-align: middle;
  }
}

::v-deep .view-reg-modal__footer {
  border-top: 1px solid #ebe9f1;
  padding: 0.5rem 1rem;
  justify-content: flex-end;
  gap: 0.5rem;
}

::v-deep .convocation-view-modal .modal-header {
  padding: 0.65rem 1.1rem;
}

::v-deep .convocation-view-modal .modal-title {
  font-size: 1rem;
  font-weight: 600;
}
</style>
